import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BRAND } from './auditTones';

/**
 * Where the reviewer is in the audit.
 *
 * The three stages are genuinely sequential - a mapping cannot be confirmed
 * before a file is read, and there are no results until it runs - so showing
 * them as a progress track sets the expectation up front rather than making the
 * page appear and disappear underneath the user.
 *
 * Colours are inline, not `bg-primary` / `text-primary`: those utilities paint
 * nothing in this theme's light mode. See BRAND in auditTones.js for why.
 *
 * The track is capped and centred. Left to span the full width it stretched the
 * connectors to several hundred pixels and the three steps read as three
 * unrelated labels stranded at opposite ends of the page.
 */

export const STEPS = [
  { key: 'source', label: 'Facility & file', hint: 'what to audit', icon: 'file-upload' },
  { key: 'mapping', label: 'Column mapping', hint: 'confirm the columns', icon: 'sliders-h' },
  { key: 'results', label: 'Results', hint: 'what differs', icon: 'tasks' }
];

const AuditSteps = ({ current }) => {
  const activeIndex = Math.max(0, STEPS.findIndex(s => s.key === current));

  return (
    <div className="d-flex align-items-center mx-auto" style={{ maxWidth: 760 }}>
      {STEPS.map((step, index) => {
        const done = index < activeIndex;
        const active = index === activeIndex;
        const filled = done || active;

        return (
          <React.Fragment key={step.key}>
            <div className="d-flex align-items-center flex-shrink-0">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: 36,
                  height: 36,
                  backgroundColor: filled ? BRAND.base : '#fff',
                  color: filled ? '#fff' : '#9da9bb',
                  border: filled ? `1px solid ${BRAND.base}` : '1px solid #d8e2ef',
                  boxShadow: active ? `0 0 0 4px ${BRAND.ring}` : 'none',
                  opacity: done ? 0.72 : 1,
                  transition: 'box-shadow .2s ease, background-color .2s ease'
                }}
              >
                <FontAwesomeIcon icon={done ? 'check' : step.icon} style={{ fontSize: '0.85rem' }} />
              </div>
              <div className="ms-2 d-none d-sm-block">
                <div
                  className="fw-semibold lh-1"
                  style={{ color: active ? BRAND.base : filled ? '#5e6e82' : '#9da9bb', fontSize: '0.85rem' }}
                >
                  {step.label}
                </div>
                <div className="fs-11 mt-1" style={{ color: '#9da9bb' }}>
                  {step.hint}
                </div>
              </div>
            </div>

            {index < STEPS.length - 1 && (
              <div
                className="flex-grow-1 mx-2 mx-md-3 rounded-pill"
                style={{ height: 3, backgroundColor: done ? BRAND.track : '#e3e6ed' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

AuditSteps.propTypes = {
  current: PropTypes.oneOf(['source', 'mapping', 'results']).isRequired
};

export default AuditSteps;
